<script setup lang="ts">
import { cloneDeep, isEqual } from "es-toolkit";
import { computed } from "vue";

import {
  TDuplicateDayProgramEvent,
  TEditRangeFormParams,
  TInsertRangeFormParams,
  TSelectedRange,
} from "@/components/programming/weekly-program/daily-program.type";
import { duplicateDayProgram } from "@/components/programming/weekly-program/duplicate-day-program";
import { RANGE_MIN_THRESHOLD, THeaterSubProgramRange } from "@/components/programming/weekly-program/site-program.type";
import { useToaster } from "@/composables/layout/use-toaster";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  ECommonProgramMetaMode,
  SubprogramRange,
  SubprogramsByApplianceCategory,
  WeeklySubprogram,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EDay } from "@/utils/date";
import { MAX_STRING_TIME, MIN_STRING_TIME, getMiddleTime, minutesDiff, stringLocalTimeSchema } from "@/utils/time";

import Skeleton from "primevue/skeleton";

import WeeklyProgram from "@/components/programming/weekly-program/weekly-program.vue";

interface IProps {
  applianceCategory: EProgramApplianceCategory;
  editable?: boolean;
  saving?: boolean;
}

interface IEmits {
  (e: "manual-change"): void;
}

const props = withDefaults(defineProps<IProps>(), {
  editable: true,
  saving: false,
});
const emit = defineEmits<IEmits>();
const subPrograms = defineModel<SubprogramsByApplianceCategory>();

const toast = useToaster();
const { t, k } = useTypedIntl();

const displayedProgram = computed<WeeklySubprogram | undefined>(() => {
  if (!subPrograms.value) return;

  return subPrograms.value[props.applianceCategory];
});

function handleManualChange(handler: () => void) {
  if (!props.editable) return;

  const previousProgram = cloneDeep(displayedProgram.value);

  handler();

  if (!isEqual(previousProgram, displayedProgram.value)) {
    emit("manual-change");
  }
}

function validateRangeValidity(currentRange: SubprogramRange): boolean {
  if (minutesDiff(currentRange.startTime, currentRange.endTime) < RANGE_MIN_THRESHOLD) {
    toast.add({
      severity: "error",
      detail: t(k.control.programming.range.error.minThreshold),
    });

    return false;
  }

  return true;
}

/**
 *
 * @param selectedRange
 * @param time is the deleted range time that is set to the previous range endtime and the next range starttime
 */
function handleDeleteRange(selectedRange: TSelectedRange, time: "start" | "end" | "mid" = "mid") {
  if (!displayedProgram.value) return;

  const tempDailyProgram = displayedProgram.value[selectedRange.day];

  if (tempDailyProgram.length <= 1) {
    toast.add({
      severity: "error",
      detail: t(
        k.control.programming.range.error.cannotDeleteLastRange({
          day: t(k.global.weekDays[selectedRange.day]),
        }),
      ),
    });
  } else if (selectedRange.index === 0) {
    tempDailyProgram.splice(selectedRange.index, 1);
    tempDailyProgram[0] = {
      ...tempDailyProgram[0],
      startTime: stringLocalTimeSchema.parse(MIN_STRING_TIME),
    };
  } else if (selectedRange.index === tempDailyProgram.length - 1) {
    tempDailyProgram.pop();
    tempDailyProgram[tempDailyProgram.length - 1] = {
      ...tempDailyProgram[tempDailyProgram.length - 1],
      endTime: stringLocalTimeSchema.parse(MAX_STRING_TIME),
    };
  } else {
    const item = tempDailyProgram[selectedRange.index];
    tempDailyProgram.splice(selectedRange.index, 1);
    if (time === "mid") {
      const middleTime = getMiddleTime(item.startTime, item.endTime);
      tempDailyProgram[selectedRange.index - 1] = {
        ...tempDailyProgram[selectedRange.index - 1],
        endTime: stringLocalTimeSchema.parse(middleTime),
      };
      tempDailyProgram[selectedRange.index] = {
        ...tempDailyProgram[selectedRange.index],
        startTime: stringLocalTimeSchema.parse(middleTime),
      };
    } else if (time === "start") {
      tempDailyProgram[selectedRange.index - 1] = {
        ...tempDailyProgram[selectedRange.index - 1],
        endTime: item.startTime,
      };
      tempDailyProgram[selectedRange.index] = {
        ...tempDailyProgram[selectedRange.index],
        startTime: item.startTime,
      };
    } else if (time === "end") {
      tempDailyProgram[selectedRange.index - 1] = {
        ...tempDailyProgram[selectedRange.index - 1],
        endTime: item.endTime,
      };
      tempDailyProgram[selectedRange.index] = {
        ...tempDailyProgram[selectedRange.index],
        startTime: item.endTime,
      };
    }
  }

  mergeCloseRangesWithSameModeByDay(selectedRange.day);
}

function insertRangeOnBusinessDays(range: TSelectedRange) {
  handleManualChange(() => {
    insertRange(range, EDay.MONDAY);
    insertRange(range, EDay.TUESDAY);
    insertRange(range, EDay.WEDNESDAY);
    insertRange(range, EDay.THURSDAY);
    insertRange(range, EDay.FRIDAY);
  });
}

function insertRangeOnWeekend(range: TSelectedRange) {
  handleManualChange(() => {
    insertRange(range, EDay.SATURDAY);
    insertRange(range, EDay.SUNDAY);
  });
}

function handleEditRange({ range, params }: TEditRangeFormParams) {
  handleManualChange(() => editRange(range, range.day));

  if (params.applyOnBusinessDays) insertRangeOnBusinessDays(range);
  if (params.applyOnWeekend) insertRangeOnWeekend(range);
}

function handleInsertRange({ range, params }: TInsertRangeFormParams) {
  handleManualChange(() => insertRange(range, range.day));

  if (params.applyOnBusinessDays) insertRangeOnBusinessDays(range);
  if (params.applyOnWeekend) insertRangeOnWeekend(range);
}

function editRange(selectedRange: TSelectedRange, day: EDay) {
  if (!displayedProgram.value) return;

  const tempDailyProgram = displayedProgram.value[day];

  const currentRange = tempDailyProgram[selectedRange.index];

  if (currentRange.startTime === MIN_STRING_TIME && selectedRange.range.startTime > MIN_STRING_TIME) {
    // The current range is the first of the day and it is getting edited to not first
    handleDeleteRange(selectedRange, "start");
    // then inserted elsewhere
    insertRange(selectedRange, day);
  } else if (currentRange.endTime === MAX_STRING_TIME && selectedRange.range.endTime < MAX_STRING_TIME) {
    // The current range is the last of the day and it is getting edited to not last
    handleDeleteRange(selectedRange, "end");
    // then inserted elsewhere
    insertRange(selectedRange, day);
  } else if (
    currentRange.startTime <= selectedRange.range.startTime &&
    currentRange.endTime >= selectedRange.range.endTime
  ) {
    // The edited range has its new startTime and endTime between its previous values

    const previousRange = tempDailyProgram[selectedRange.index - 1];
    const nextRange = tempDailyProgram[selectedRange.index + 1];

    if (!validateRangeValidity(selectedRange.range)) return;

    tempDailyProgram[selectedRange.index] = {
      ...selectedRange.range,
    };

    if (previousRange) {
      tempDailyProgram[selectedRange.index - 1] = {
        ...previousRange,
        endTime: stringLocalTimeSchema.parse(selectedRange.range.startTime),
      };
    }

    if (nextRange) {
      tempDailyProgram[selectedRange.index + 1] = {
        ...nextRange,
        startTime: stringLocalTimeSchema.parse(selectedRange.range.endTime),
      };
    }
  } else if (
    selectedRange.range.startTime < currentRange.startTime &&
    selectedRange.range.endTime >= currentRange.startTime &&
    selectedRange.range.endTime < currentRange.endTime
  ) {
    // The edited range is on the left side of the current range, with its endTime included in current range
    // delete and save selected endtime to set next range starttime
    handleDeleteRange(selectedRange, "start");
    // then insert
    insertRange(selectedRange, day);
  } else if (
    selectedRange.range.endTime > currentRange.endTime &&
    selectedRange.range.startTime > currentRange.startTime &&
    selectedRange.range.startTime <= currentRange.endTime
  ) {
    // The edited range is on the right side of the current range, with its startTime included in current range
    // delete and save selected starttime to set previous range endtime
    handleDeleteRange(selectedRange, "end");
    // then insert
    insertRange(selectedRange, day);
  } else if (
    (selectedRange.range.startTime < currentRange.startTime && selectedRange.range.endTime <= currentRange.startTime) ||
    (selectedRange.range.startTime >= currentRange.endTime && selectedRange.range.endTime > currentRange.endTime)
  ) {
    // The edited range has its new startTime and endTime completely outside its previous values, so it gets deleted
    handleDeleteRange(selectedRange);
    // then inserted elsewhere
    insertRange(selectedRange, day);
  } else if (
    selectedRange.range.startTime <= currentRange.startTime &&
    selectedRange.range.endTime >= currentRange.startTime
  ) {
    // The edited range has its previous startTime and endTime included in its new values
    insertRange(selectedRange, day);
  }

  mergeCloseRangesWithSameModeByDay(day);
}

function insertRange(selectedRange: TSelectedRange, day: EDay) {
  if (!displayedProgram.value) return;

  const tempDailyProgram = displayedProgram.value[day];

  const result: Array<SubprogramRange> = [];

  try {
    tempDailyProgram.forEach((range) => {
      if (range.endTime <= selectedRange.range.startTime || range.startTime >= selectedRange.range.endTime) {
        // No collisions
        result.push(range);
        return;
      }

      if (range.startTime > selectedRange.range.startTime && range.endTime < selectedRange.range.endTime) {
        // We totally overlap existing range, so we erase existing range
        return;
      }

      if (range.startTime < selectedRange.range.startTime && range.endTime > selectedRange.range.endTime) {
        // We are inside an existing range, we split existing range into two pieces
        const firstRange = {
          ...range,
          startTime: range.startTime,
          endTime: selectedRange.range.startTime,
        };

        const secondRange = {
          ...range,
          startTime: selectedRange.range.endTime,
          endTime: range.endTime,
        };

        result.push(firstRange);
        result.push(secondRange);

        return;
      }

      if (
        range.startTime < selectedRange.range.startTime &&
        range.endTime > selectedRange.range.startTime &&
        range.endTime <= selectedRange.range.endTime
      ) {
        // Selected range starts within range and ends after range, so we shortened existing range to match the new range start time

        result.push({
          ...range,
          startTime: range.startTime,
          endTime: selectedRange.range.startTime,
        });
        return;
      }

      if (
        range.startTime < selectedRange.range.endTime &&
        range.startTime >= selectedRange.range.startTime &&
        range.endTime > selectedRange.range.endTime
      ) {
        // Selected range starts before range and ends within it, so we shortened existing range to match the new range end time
        result.push({
          ...range,
          startTime: selectedRange.range.endTime,
          endTime: range.endTime,
        });
      }
    });

    result.push(selectedRange.range);

    const isRangeInvalid = result.some((range) => {
      return minutesDiff(range.startTime, range.endTime) < RANGE_MIN_THRESHOLD;
    });

    if (isRangeInvalid) {
      throw new Error("Every range should last at least 30 minutes");
    }

    result.sort((firstRange, secondRange) => {
      return firstRange.startTime < secondRange.startTime ? -1 : 1;
    });

    displayedProgram.value[day] = result;
  } catch (e) {
    console.error(e);
    toast.add({ severity: "error", detail: t(k.control.programming.range.error.minThreshold) });
  }

  mergeCloseRangesWithSameModeByDay(day);
}

function mergeCloseRangesWithSameModeByDay(day: EDay) {
  if (!displayedProgram.value) return;

  const tempDailyProgram = displayedProgram.value[day];
  const result: Array<SubprogramRange> = [tempDailyProgram[0]];

  for (let i = 1; i < tempDailyProgram.length; i++) {
    const previousRange = result[result.length - 1];
    const currentRange = tempDailyProgram[i];

    const hasSameModeExceptTemperature =
      currentRange.metaMode !== ECommonProgramMetaMode.TEMP && previousRange.metaMode === currentRange.metaMode;

    const hasSameTemperature =
      currentRange.metaMode === ECommonProgramMetaMode.TEMP &&
      previousRange.metaMode === ECommonProgramMetaMode.TEMP &&
      (currentRange as THeaterSubProgramRange).temperature === (previousRange as THeaterSubProgramRange).temperature;

    if (hasSameModeExceptTemperature || hasSameTemperature) {
      result[result.length - 1] = {
        ...previousRange,
        endTime: currentRange.endTime,
      };
    } else {
      result.push(currentRange);
    }
  }

  displayedProgram.value[day] = result;
}

async function handleDuplicateDayProgram(event: TDuplicateDayProgramEvent): Promise<boolean> {
  if (!props.editable || !subPrograms.value || !displayedProgram.value) return false;

  const previousSubPrograms = cloneDeep(subPrograms.value);
  const updatedSubPrograms = {
    ...subPrograms.value,
    [props.applianceCategory]: duplicateDayProgram(displayedProgram.value, event.sourceDay, event.targetDays),
  };

  if (isEqual(previousSubPrograms, updatedSubPrograms)) return true;

  subPrograms.value = updatedSubPrograms;

  emit("manual-change");
  return true;
}
</script>

<template>
  <div v-if="subPrograms">
    <div class="flex overflow-auto">
      <WeeklyProgram
        class="!rounded-none !p-0"
        :appliance-category="props.applianceCategory"
        :duplicate-day="handleDuplicateDayProgram"
        :duplicate-day-loading="props.saving"
        :editable="props.editable"
        :weekly-program="displayedProgram"
        @delete="handleManualChange(() => handleDeleteRange($event))"
        @edit="handleEditRange($event)"
        @insert="handleInsertRange($event)"
      />
    </div>
  </div>

  <div
    v-else
    class="flex flex-col gap-6"
  >
    <Skeleton class="!h-[4rem]" />
    <Skeleton class="!h-[20rem]" />
  </div>
</template>
